import { useEffect, useState } from 'react';
import { Film, Image as ImageIcon, Play, X } from 'lucide-react';
import type { ReportingCreative } from '../../types/reporting';
import {
  creativeKind,
  creativeLinkOut,
  showPlaceholder,
  type CreativeKind,
} from './creativeThumbnail';

const INLINE_SIZE = 28; // px — small + lazy so a big ad list stays snappy (brief).

// The neutral placeholder tile — a photo/film icon on a muted square. Rendered
// instead of a broken-image glyph whenever there's no usable thumbnail (missing OR
// an expired-CDN load error), so a historic/deleted ad reads as intentional.
function PlaceholderTile({
  kind,
  size,
  label,
}: {
  kind: CreativeKind;
  size: number;
  label: string;
}) {
  const Icon = kind === 'video' ? Film : ImageIcon;
  return (
    <span
      role="img"
      aria-label={label}
      className="inline-flex shrink-0 items-center justify-center rounded bg-slate-100 text-slate-400"
      style={{ width: size, height: size }}
    >
      <Icon style={{ width: Math.round(size * 0.5), height: Math.round(size * 0.5) }} aria-hidden />
    </span>
  );
}

// An <img> that degrades to the placeholder tile on load error (the Meta CDN URLs
// are short-lived). `square` sizes the inline tile; the modal image fills its box.
function CreativeImage({
  src,
  kind,
  size,
  placeholderLabel,
  className,
}: {
  src: string;
  kind: CreativeKind;
  size?: number;
  placeholderLabel: string;
  className?: string;
}) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return <PlaceholderTile kind={kind} size={size ?? INLINE_SIZE} label={placeholderLabel} />;
  }
  return (
    <img
      src={src}
      alt="Ad creative"
      loading="lazy"
      onError={() => setErrored(true)}
      className={className}
      style={size ? { width: size, height: size, objectFit: 'cover' } : undefined}
    />
  );
}

// The enlarge modal: the larger creative (image, or a <video> when the ad is a
// video), the ad body copy, and a link out. Light + read-only — a preview, not an
// editor. Dismiss via ESC, the backdrop, or the close button.
function EnlargeModal({
  creative,
  name,
  onClose,
}: {
  creative: ReportingCreative;
  name: string | null;
  onClose: () => void;
}) {
  const kind = creativeKind(creative);
  const link = creativeLinkOut(creative);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={`Creative preview${name ? `: ${name}` : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-4 py-2.5">
          <h2 className="min-w-0 truncate text-sm font-semibold text-slate-900" title={name ?? undefined}>
            {name ?? 'Ad creative'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="ml-3 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex items-center justify-center bg-slate-50 p-3">
          {/* ad_creatives.video_url is a Facebook WATCH PAGE (HTML), not a direct
              media file, so a <video> can't stream it (empty/broken player). For
              video creatives too we show the poster/thumbnail larger — the play
              badge on the row tile marks it a video, and the link out below opens
              it on Facebook / Instagram. */}
          {creative.thumbnailUrl ? (
            <CreativeImage
              src={creative.thumbnailUrl}
              kind={kind}
              placeholderLabel="No creative preview"
              className="max-h-[60vh] w-full rounded object-contain"
            />
          ) : (
            <PlaceholderTile kind={kind} size={96} label="No creative preview" />
          )}
        </div>

        {creative.body && creative.body.trim() !== '' && (
          <p className="whitespace-pre-wrap break-words px-4 py-3 text-sm text-slate-700">
            {creative.body}
          </p>
        )}

        {link && (
          <div className="border-t border-slate-200 px-4 py-2.5">
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              {link.label} ↗
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// The inline ad-row thumbnail (~28px): a real thumbnail from the creative, a play
// badge overlaid when it's a video, or the neutral placeholder when there's no
// usable thumbnail. Clicking opens the enlarge modal. Rendered ONLY on ad rows
// (breakdownCell gates on dim==='ad'); `creative` may be null (ad deleted from Meta
// → placeholder). Lazy image load keeps a big ad list snappy.
export function AdCreativeThumbnail({
  creative,
  name,
}: {
  creative: ReportingCreative | null;
  name: string | null;
}) {
  const [open, setOpen] = useState(false);
  const kind = creativeKind(creative);
  const usePlaceholder = showPlaceholder(creative);
  const label = name ? `Preview creative for ${name}` : 'Preview creative';

  // Something worth enlarging? An ad deleted from Meta (no creative row, or an
  // all-null block after a LEFT JOIN miss) has nothing — render an inert tile so
  // clicking doesn't open an empty modal.
  const canEnlarge = Boolean(
    creative &&
      (creative.thumbnailUrl || creative.videoUrl || creative.body || creativeLinkOut(creative)),
  );

  const tile = (
    <>
      {usePlaceholder || !creative?.thumbnailUrl ? (
        <PlaceholderTile kind={kind} size={INLINE_SIZE} label="No creative preview" />
      ) : (
        <CreativeImage
          src={creative.thumbnailUrl}
          kind={kind}
          size={INLINE_SIZE}
          placeholderLabel="No creative preview"
        />
      )}
      {kind === 'video' && (
        <span
          title="Video"
          className="absolute bottom-0 right-0 flex h-3.5 w-3.5 items-center justify-center rounded-tl bg-slate-900/70 text-white"
        >
          <Play className="h-2 w-2 fill-current" aria-hidden />
        </span>
      )}
    </>
  );

  const tileClass =
    'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded ring-1 ring-slate-200';

  if (!canEnlarge) {
    return <span className={tileClass} style={{ width: INLINE_SIZE, height: INLINE_SIZE }}>{tile}</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={`${tileClass} hover:ring-slate-300`}
        style={{ width: INLINE_SIZE, height: INLINE_SIZE }}
      >
        {tile}
      </button>
      {open && creative && (
        <EnlargeModal creative={creative} name={name} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
